import { isSameOriginUrl } from '@Core/lib/tasks/utils/navigate';
import { Link as InertiaLink } from '@inertiajs/react';
import { useBreadcrumbHrefResolver } from '@Pages/pageRuntime/breadcrumbLocations';
import type { ButtonProps } from '@Pages/pageRuntime/components/ui/Button';
import { buttonVariants } from '@Pages/pageRuntime/components/ui/buttonVariants';
import {
  DropdownMenu as UiDropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem as UiDropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@Pages/pageRuntime/components/ui/DropdownMenu';
import { SafeDynamicIcon } from '@Pages/pageRuntime/components/ui/SafeDynamicIcon';
import { cn } from '@Pages/pageRuntime/lib/utils';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import React from 'react';

type LinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  /**
   * Destination URL or anchor. For another page in this workspace, resolve it from the
   * page's stable id with `Sdk.utils.page.url(pageId, params)` and pass the non-null
   * result — never a hardcoded route string. Literal values are only for in-page anchors
   * (`#pricing`), absolute external URLs, and `mailto:`/`tel:` addresses.
   */
  href: string;

  /**
   * Open in a new tab and add safe rel attributes.
   */
  external?: boolean;

  /**
   * Link contents.
   */
  children?: React.ReactNode;
};

type ButtonLinkProps = Omit<LinkProps, 'className'> &
  Pick<ButtonProps, 'variant' | 'size'> & {
    className?: string;
  };

type DropdownMenuItem = {
  /**
   * Item label.
   */
  label: React.ReactNode;

  /**
   * Optional URL. When present the item renders as a link. Resolve a workspace page with
   * `Sdk.utils.page.url(pageId, params)` instead of hardcoding a route string.
   */
  href?: string;

  /**
   * Optional Lucide icon name shown beside the label.
   */
  icon?: string;

  /**
   * Disable the item.
   */
  disabled?: boolean;

  /**
   * Style the item as a destructive action.
   */
  destructive?: boolean;

  /**
   * Called when the item is selected.
   */
  onSelect?: () => void;
};

type DropdownMenuProps = {
  /**
   * Element that opens the menu.
   */
  trigger: React.ReactNode;

  /**
   * Menu item definitions.
   */
  items: DropdownMenuItem[];

  /**
   * Optional menu heading.
   */
  label?: React.ReactNode;

  /**
   * Menu alignment relative to the trigger.
   */
  align?: 'start' | 'center' | 'end';

  className?: string;
};

type BreadcrumbItem = {
  /**
   * Item label.
   */
  label: React.ReactNode;

  /**
   * Optional URL. Omit on the final item to mark it as the current page. Resolve a
   * workspace page with `Sdk.utils.page.url(pageId, params)` instead of hardcoding a route
   * string.
   */
  href?: string;
};

type BreadcrumbProps = {
  /**
   * Ordered breadcrumb items. The last item is treated as the current page when `href` is
   * omitted.
   */
  items: BreadcrumbItem[];

  className?: string;
};

type IconProps = {
  /**
   * Lucide icon name in camelCase, PascalCase, or kebab-case.
   */
  name?: string | null;

  /**
   * Additional CSS classes.
   */
  className?: string;

  /**
   * Icon size in pixels.
   */
  size?: number;

  /**
   * SVG stroke width.
   */
  strokeWidth?: number;

  /**
   * Direct color override (any CSS color). Defaults to `currentColor`.
   */
  color?: string;
};

/**
 * A theme-aware text link for page navigation, external URLs, and anchors. Use for
 * simple links; use ButtonLink when the link should look like an action button.
 * Same-origin URLs use Inertia. Other origins use normal browser navigation in
 * the current tab. Set `external` to open a new tab.
 *
 * Address another workspace page by its stable id through `Sdk.utils.page.url()`, which
 * returns `string | null`, and render the link only when it resolves. A hardcoded route
 * string is not tracked as a page reference and breaks silently when the route changes.
 *
 * @example
 * // Link to another page by id
 * const studentsUrl = Sdk.utils.page.url(STUDENTS_PAGE_ID);
 * const studentsLink = studentsUrl ? <Sdk.Components.Link href={studentsUrl}>View students</Sdk.Components.Link> : null;
 *
 * @example
 * // Link to a detail page, passing its route params
 * const detailUrl = Sdk.utils.page.url(STUDENT_DETAIL_PAGE_ID, { student: row.id });
 * const detailLink = detailUrl ? <Sdk.Components.Link href={detailUrl}>{row.name}</Sdk.Components.Link> : null;
 *
 * @example
 * // An in-page anchor is the one destination that stays literal
 * <Sdk.Components.Link href="#pricing">Pricing</Sdk.Components.Link>
 */
export function Link({ href, external = false, className, children, ...props }: LinkProps) {
  const linkClassName = cn(
    'font-medium text-primary underline-offset-4 hover:underline',
    className
  );

  if (external) {
    return (
      <a
        href={href}
        className={linkClassName}
        target={props.target ?? '_blank'}
        rel={props.rel ?? 'noreferrer noopener'}
        {...props}
      >
        {children}
      </a>
    );
  }

  if (!isSameOriginUrl(href)) {
    return (
      <a href={href} className={linkClassName} {...props}>
        {children}
      </a>
    );
  }

  return (
    <InertiaLink href={href} className={linkClassName} {...(props as Record<string, unknown>)}>
      {children}
    </InertiaLink>
  );
}

/**
 * A link styled with the standard Diagonal button variants. Use for navigational actions;
 * use Button for actions handled in code. Set `external` to open the destination in a new
 * tab as a plain anchor (with safe `rel` attributes) instead of an Inertia SPA navigation.
 * URLs on another origin use a plain anchor in the current tab by default.
 *
 * For a workspace page, resolve `href` from the page's stable id with `Sdk.utils.page.url()`
 * and render the link only when it resolves. Never hardcode a route string: it is not
 * tracked as a page reference and breaks silently when the route changes.
 *
 * @example
 * // Link to another page by id
 * const applicationsUrl = Sdk.utils.page.url(APPLICATIONS_PAGE_ID);
 * const openApplications = applicationsUrl
 *   ? <Sdk.Components.ButtonLink href={applicationsUrl} variant="default">Open applications</Sdk.Components.ButtonLink>
 *   : null;
 *
 * @example
 * // Open an external URL in a new tab
 * <Sdk.Components.ButtonLink href="https://example.com" external variant="outline">View docs</Sdk.Components.ButtonLink>
 */
export function ButtonLink({
  href,
  external = false,
  variant,
  size,
  className,
  children,
  ...props
}: ButtonLinkProps) {
  const buttonClassName = cn(buttonVariants({ variant, size }), className);

  if (external) {
    return (
      <a
        href={href}
        className={buttonClassName}
        target={props.target ?? '_blank'}
        rel={props.rel ?? 'noreferrer noopener'}
        {...props}
      >
        {children}
      </a>
    );
  }

  if (!isSameOriginUrl(href)) {
    return (
      <a href={href} className={buttonClassName} {...props}>
        {children}
      </a>
    );
  }

  return (
    <InertiaLink href={href} className={buttonClassName} {...(props as Record<string, unknown>)}>
      {children}
    </InertiaLink>
  );
}

/**
 * A compact menu for row actions, overflow actions, and grouped choices. Use for
 * secondary actions; keep labels short and avoid nesting menus in generated pages.
 *
 * @example
 * const detailUrl = Sdk.utils.page.url(RECORD_DETAIL_PAGE_ID, { record: row.id });
 * <Sdk.Components.DropdownMenu
 *   trigger={<Sdk.Components.Button variant="outline">Actions</Sdk.Components.Button>}
 *   items={[{ label: 'View', href: detailUrl ?? undefined }, { label: 'Archive', onSelect: archive }]}
 * />
 */
export function DropdownMenu({
  trigger,
  items,
  label,
  align = 'end',
  className
}: DropdownMenuProps) {
  return (
    <UiDropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} className={className}>
        {label ? <DropdownMenuLabel>{label}</DropdownMenuLabel> : null}
        {label && items.length > 0 ? <DropdownMenuSeparator /> : null}
        {items.map((item, index) => {
          const content = (
            <>
              {item.icon ? <SafeDynamicIcon name={item.icon} className="size-4" /> : null}
              <span>{item.label}</span>
            </>
          );

          return item.href ? (
            <UiDropdownMenuItem key={index} asChild disabled={item.disabled}>
              <a
                href={item.href}
                className={cn(item.destructive ? 'text-destructive focus:text-destructive' : null)}
              >
                {content}
              </a>
            </UiDropdownMenuItem>
          ) : (
            <UiDropdownMenuItem
              key={index}
              disabled={item.disabled}
              className={cn(item.destructive ? 'text-destructive focus:text-destructive' : null)}
              onSelect={item.onSelect}
            >
              {content}
            </UiDropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </UiDropdownMenu>
  );
}

/**
 * A small breadcrumb trail for nested detail pages and admin flows. Use near the top of
 * detail pages when parent context matters.
 *
 * A crumb to a page that the visitor opened earlier in the browser tab links to the URL they
 * last viewed there, query params included. A list page that keeps its filters, sort, and page
 * in the URL, such as a `ResourceTable` with `urlStateKey` or `Sdk.hooks.useQueryParams`, opens
 * as the visitor left it. An `href` with its own query string or hash links to that exact
 * location.
 *
 * @example
 * const studentsUrl = Sdk.utils.page.url(STUDENTS_PAGE_ID);
 * <Sdk.Components.Breadcrumb items={[{ label: 'Students', href: studentsUrl ?? undefined }, { label: student.name }]} />
 */
export function Breadcrumb({ items, className }: BreadcrumbProps) {
  const resolveHref = useBreadcrumbHrefResolver();

  return (
    <nav aria-label="breadcrumb" className={className}>
      <ol className="flex flex-wrap items-center gap-1.5 text-sm break-words text-muted-foreground">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const href = item.href && !isLast ? resolveHref(item.href) : null;

          return (
            <React.Fragment key={index}>
              <li className="inline-flex items-center gap-1.5">
                {href ? (
                  isSameOriginUrl(href) ? (
                    <InertiaLink className="transition-colors hover:text-foreground" href={href}>
                      {item.label}
                    </InertiaLink>
                  ) : (
                    <a className="transition-colors hover:text-foreground" href={href}>
                      {item.label}
                    </a>
                  )
                ) : (
                  <span
                    aria-current={isLast ? 'page' : undefined}
                    className="font-normal text-foreground"
                  >
                    {item.label}
                  </span>
                )}
              </li>
              {!isLast ? (
                <li aria-hidden="true" className="[&>svg]:size-3.5">
                  <ChevronRight />
                </li>
              ) : null}
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * A standalone, theme-aware Lucide icon rendered by `name`, with a circular fallback for
 * unknown names. Drop it into any custom layout — buttons, table cells, nav links, inline
 * with text. Use alongside a text label unless the icon is purely decorative.
 *
 * @example
 * // Inline with text
 * <Sdk.Components.Icon name="Users" size={16} className="text-muted-foreground" />
 *
 * @example
 * // Inside a custom button
 * <button className="flex items-center gap-2">
 *   <Sdk.Components.Icon name="Home" size={16} />
 *   <span>Dashboard</span>
 * </button>
 *
 * @example
 * // As a status indicator in a table cell
 * <td className="flex items-center gap-2">
 *   <Sdk.Components.Icon name="CheckCircle" size={14} className="text-green-500" />
 *   Active
 * </td>
 */
export function Icon({ name, className, size, strokeWidth, color }: IconProps) {
  return (
    <SafeDynamicIcon
      name={name}
      className={className}
      size={size}
      strokeWidth={strokeWidth}
      color={color}
    />
  );
}
